import {CHANGE} from '@megaphonenpm/mputils/src/constants.js';
import {WebPollMonitor} from './WebPollMonitor.js';
import {isEmpty, notNil} from '@megaphonenpm/mputils/src/utils.js';
import {STATE_SCENE} from './constants.js';
import {WebPollService} from './WebPollService.js';
import {get} from 'lodash-es';

/**
 * In Web Poll scenes, WebPollAppStateSynchronizer keeps the AppState up to date with web poll data
 * loaded from the remote Web Poll Service.
 */
export class WebPollAppStateSynchronizer {
  constructor (appState, dataStore, webpollService) {
    this.appState = appState;
    this.dataStore = dataStore;
    this.webpollService = webpollService;
    this.monitor = new WebPollMonitor(null, dataStore);

    this.registerForAppStateChanges();
    this.registerForWebPollMonitorChanges();
  }

  registerForAppStateChanges () {
    this.appState.on(CHANGE, e => {
      // At the start of each scene, if the scene's product is 'webpoll', then update
      // appState's 'votes' property to the most recently retrieved poll_results for the scene's
      // web poll.
      if (e.detail.prop === 'state') {
        if (e.detail.value === STATE_SCENE) {
          // Note that for both "web poll scenes" and "prediction question scenes", the scene product
          // name is "WebPoll". Hence, because isCurrentSceneWebPoll() uses the scene product name to
          // determine whether or not a scene is a web poll, so isCurrentSceneWebPoll() returns true
          // for BOTH "web poll scenes" AND "prediction question scenes" (but NOT "prediction scoreboard"
          // scenes). Hence, in the following conditional, there's no need to check separately whether
          // the current scene is of type prediction. The desired outcome (enable monitoring for both
          // web polls and prediction questions) is achieved with the single call to
          // isCurrentSceneWebPoll(), despite the fact that the method name doesn't mention "prediction".
          if (this.appState.isCurrentSceneWebPoll()) {
            // Set webpoll-related appState properties without dispatching CHANGE (quiet = true)
            // because the current appState.state assignment will trigger a single, central CHANGE
            // event that views handle and render. Setting the properties quietly prevents redundant
            // rendering. If any webpoll-related appState properties, such as .votes change AFTER
            // this state change, then those assignments should dispatch CHANGE (quiet = false).
            this.syncCurrentWebPoll(true);
            this.monitor.setWebPollInfo(this.appState.getCurrentSceneSrcID(), get(this.appState.scene, 'type'));

            // Begin monitoring the web poll for changes
            let webpollUpdateFrequency = get(this.dataStore, 'deploymentSettings.screen.webpoll_update_frequency', null);
            if (notNil(webpollUpdateFrequency)) {
              // Never update the web poll more often than every 2 seconds.
              if (webpollUpdateFrequency < 2000) {
                webpollUpdateFrequency = 2000;
              }
              // Apply supplied "Web Poll Update Frequency" project setting
              this.monitor.frequency = webpollUpdateFrequency;
            }
            this.monitor.start();

            if (!this.dataStore.webpolls.has(this.appState.getCurrentSceneSrcID())) {
              // The dataStore does not contain the web poll record for this scene, so update the
              // web poll immediately by calling the WebPollMonitor instances's doTask() method.
              // (This case occurs when the client joins the MRTS run during a web poll scene from
              // a previous playlist, AFTER the current playlist has changed. If the new playlist
              // does not contain the web poll, the dataStore will not contain the web poll record.)
              this.monitor.doTask();
            }
          }
        } else {
          this.monitor.stop();
        }
      }
    });
  }

  registerForWebPollMonitorChanges () {
    this.monitor.on(CHANGE, (e) => {
      this.syncCurrentWebPoll();
    });
  }

  /**
   * Updates webpoll-related appState properties to match the latest available pollRecord for the
   * current scene.
   *
   * @param quiet If true, don't dispatch CHANGE events when updating properties.
   */
  syncCurrentWebPoll (quiet) {
    this.syncCurrentWebPollVotes(quiet);
    this.syncCurrentWebPollState(quiet);
    this.syncCurrentWebpollScene(quiet);
  }

  /**
   * Checks if the current scene's product is 'webpoll', and if it is, assigns this
   * AppState object's 'votes' property the current poll_results for the scene's web poll.
   */
  syncCurrentWebPollVotes (quiet) {
    let pollRecord = this.getCurrentWebPollRecord();

    if (notNil(pollRecord)) {
      // The "page scene ID" is the ID of the scene in the MegaPage that was added to the playlist,
      // not to be confused with the MRTS playlist scene's ID.
      // Note that when the votes change for ANY poll in the page being synced, that change will
      // trigger syncCurrentWebPollVotes() even if the poll is not the current scene's poll (i.e.,
      // the poll that was added to the MRTS playlist). This unnecessary syncCurrentWebPollVotes()
      // invocation, however, is harmless because appState.setVotesForWebPoll() will not trigger a
      // CHANGE event if the votes haven't changed. As a technical improvement, UpdateWebPollTask
      // could, in theory, ignore vote-result changes that do not affect the current appState's
      // webpollScene, but that optimization is considered premature as of Jan 16, 2025.
      this.appState.setVotesForWebPoll(new WebPollService().getVotesByIndex(pollRecord, this.appState.getCurrentPageSceneID()), quiet);
    }
  }

  syncCurrentWebPollState (quiet = false) {
    let pollRecord = this.getCurrentWebPollRecord();

    if (notNil(pollRecord)) {
      let newWebpollState = this.webpollService.getStateForWebPoll(pollRecord);
      this.appState.setPrimitiveProperty('webpollState', newWebpollState, quiet);
    }
  }

  syncCurrentWebpollScene (quiet = false) {
    let pollRecord = this.getCurrentWebPollRecord();
    if (notNil(pollRecord)) {
      let currentWebpollScenes = pollRecord[this.webpollService.getContentFieldNameForRecord(pollRecord)].scenes;
      let currentWebpollSceneID = this.appState.getCurrentPageSceneID();
      let currentWebpollSceneIndex = isEmpty(currentWebpollSceneID) ? 0 : currentWebpollScenes.findIndex(scene => scene.id === currentWebpollSceneID);
      let currentWebpollScene = currentWebpollScenes[currentWebpollSceneIndex];
      this.appState.setCompositeProperty('webpollScene', currentWebpollScene, quiet);
    } else {
      // No data available for the current scene's web poll (possibly because an attempt to load
      // the poll failed due to, for example, a server 429 error).
      this.appState.setCompositeProperty('webpollScene', null, quiet);
    }
  }

  getCurrentWebPollRecord () {
    let pollID = this.appState.getCurrentSceneSrcID();
    return this.dataStore.webpolls.get(pollID);
  }
}


